export function parseReportData(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return null;
}

export function downloadOdcBusinessReport(report: Record<string, unknown> | null) {
  if (!report) return;
  const lines: string[] = [];

  const es = report.executive_summary as Record<string, string> | undefined;
  const ar = report.agent_recommendations as Record<string, unknown> | undefined;
  const cs = report.content_strategy as Record<string, unknown> | undefined;
  const gr = report.growth_roadmap as Record<string, Record<string, unknown>> | undefined;
  const roi = report.roi_projection as Record<string, unknown> | undefined;
  const plan = report.recommended_plan as Record<string, unknown> | undefined;

  lines.push('═'.repeat(60));
  lines.push('ODC — ONLINEDIGITALCARES.COM');
  lines.push('BUSINESS INTELLIGENCE REPORT');
  lines.push('═'.repeat(60));
  lines.push('');
  lines.push(`Business: ${es?.headline || ''}`);
  lines.push(`Generated: ${new Date().toLocaleDateString('en-IN')}`);
  lines.push(`Powered by: ODC AI + ODC Platform`);
  lines.push('');

  lines.push('─'.repeat(60));
  lines.push('EXECUTIVE SUMMARY');
  lines.push('─'.repeat(60));
  lines.push(es?.overview || '');
  lines.push('');
  lines.push(`Market Opportunity: ${es?.market_opportunity || ''}`);
  lines.push(`Key Insight: ${es?.key_insight || ''}`);
  lines.push('');

  lines.push('─'.repeat(60));
  lines.push('AI AGENT RECOMMENDATIONS');
  lines.push('─'.repeat(60));
  lines.push('Recommended agents: see Essential, Recommended, and Optional sections below.');
  lines.push('');
  lines.push('ESSENTIAL AGENTS (Activate Immediately):');
  const essential = (ar?.essential as Array<Record<string, unknown>> | undefined) ?? [];
  essential.forEach((a, i) => {
    lines.push(`${i + 1}. ${String(a.icon || '')} ${String(a.name || '')} (${String(a.category || '')})`);
    lines.push(`   Why: ${String(a.why_needed || '')}`);
    lines.push(`   Impact: ${String(a.expected_impact || '')}`);
    lines.push('');
  });

  lines.push('─'.repeat(60));
  lines.push('3-MONTH CONTENT STRATEGY');
  lines.push('─'.repeat(60));
  lines.push(`Plan: ${String(cs?.plan_title || '')}`);
  lines.push(`Total Posts: ${Number(cs?.total_posts) || 0}`);
  lines.push(`Brand Voice: ${String(cs?.brand_voice || '')}`);
  lines.push('');
  lines.push('Key Themes:');
  const themes = (cs?.key_themes as string[] | undefined) ?? [];
  themes.forEach((t, i) => {
    lines.push(`${i + 1}. ${t}`);
  });
  lines.push('');

  const phases = (cs?.phases as Array<Record<string, unknown>> | undefined) ?? [];
  phases.forEach((phase) => {
    lines.push(
      `PHASE ${Number(phase.phase_number)}: ${String(phase.phase_name)} (${String(phase.duration)})`
    );
    lines.push(`Focus: ${String(phase.focus)}`);
    const pillars = (phase.content_pillars as string[] | undefined) ?? [];
    lines.push(`Content Pillars: ${pillars.join(' | ')}`);
    lines.push('');
    const weeks = (phase.weekly_posts as Array<Record<string, unknown>> | undefined) ?? [];
    weeks.forEach((week) => {
      lines.push(`  Week ${Number(week.week)}: ${String(week.theme)}`);
      const days = ['monday', 'wednesday', 'friday', 'sunday'] as const;
      days.forEach((day) => {
        const post = week[day] as Record<string, unknown> | undefined;
        if (post) {
          lines.push(`    ${day.toUpperCase()}: [${String(post.platform)}] ${String(post.type)}`);
          lines.push(`    Topic: ${String(post.topic)}`);
          lines.push(`    Caption: ${String(post.caption || post.message || '')}`);
          const tags = post.hashtags as string[] | undefined;
          if (tags?.length) lines.push(`    Tags: ${tags.map((h) => `#${h}`).join(' ')}`);
          lines.push('');
        }
      });
    });
    const kpis = (phase.kpis as string[] | undefined) ?? [];
    lines.push(`  KPIs: ${kpis.join(' | ')}`);
    lines.push('');
  });

  lines.push('─'.repeat(60));
  lines.push('GROWTH ROADMAP');
  lines.push('─'.repeat(60));
  const roadmap = gr || {};
  ;['month_1', 'month_2', 'month_3', 'month_6', 'month_12'].forEach((m) => {
    const rm = roadmap[m];
    if (!rm) return;
    lines.push(`${m.replace('_', ' ').toUpperCase()}: ${String(rm.title)}`);
    lines.push(`Focus: ${String(rm.focus)}`);
    lines.push(`Expected: ${String(rm.expected_results)}`);
    const acts = (rm.key_actions as string[] | undefined) ?? [];
    acts.forEach((a) => lines.push(`  • ${a}`));
    lines.push('');
  });

  lines.push('─'.repeat(60));
  lines.push('ROI PROJECTIONS');
  lines.push('─'.repeat(60));
  if (roi?.month_1_projection) {
    const m1 = roi.month_1_projection as Record<string, unknown>;
    const m3 = roi.month_3_projection as Record<string, unknown> | undefined;
    const m6 = roi.month_6_projection as Record<string, unknown> | undefined;
    lines.push(`Month 1: ${m1.leads} leads → ${m1.revenue_potential}`);
    lines.push(`Month 3: ${m3?.leads} leads → ${m3?.revenue_potential}`);
    lines.push(`Month 6: ${m6?.leads} leads → ${m6?.revenue_potential}`);
  }
  lines.push('');

  lines.push('─'.repeat(60));
  lines.push('RECOMMENDED PLAN');
  lines.push('─'.repeat(60));
  lines.push(`Plan: ${String(plan?.plan_name)} — ${String(plan?.monthly_cost)}/month`);
  lines.push('AI agents: included per your recommended plan tier.');
  lines.push(`Why: ${String(plan?.why_this_plan)}`);
  lines.push(`Upgrade when: ${String(plan?.upgrade_trigger)}`);
  lines.push('');

  lines.push('═'.repeat(60));
  lines.push('Generated by ODC AI — OnlineDigitalCares.com');
  lines.push('The Future of AI Marketing — Powerful AI Agents — One Unified Platform');
  lines.push('═'.repeat(60));

  const text = lines.join('\n');
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ODC-Business-Report-${new Date().toISOString().split('T')[0]}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}
